<script lang="ts">
  import type {
    SearchResult,
    Stakeholder,
    StakeholderType
  } from '@gat-ship/types'
  import { stakeholdersSearchRequest } from '@gat-ship/stakeholder-search'
  export interface SelectStakeholderProps extends Omit<
    ComboboxFieldProps,
    'items' | 'modelValue'
  > {
    items?: SelectStakeholderItem[]
    enableSearch?: boolean
    enableRecentlyVisited?: boolean
  }

  export interface SelectStakeholderItem {
    id: string
    name: string
    stakeholderType: StakeholderType
  }
</script>

<script setup lang="ts">
  import type {
    ComboboxFieldProps,
    ComboboxFieldItem
  } from '@gat-ship/vue-components'
  import {
    Command,
    CommandList,
    CommandGroup,
    CommandItem
  } from '@gat-ship/vue-components'
  import { computed, useTemplateRef, watch } from 'vue'
  import { useStakeholderRecentlyVisited } from '@gat-ship/stakeholder-search'
  import StakeholderSelectItem from './StakeholderSelectItem.vue'
  import { IconSearch } from '@gat-ship/icons'
  import { globalUtils } from '@gat-ship/utils'
  import { ComboboxField } from '@gat-ship/vue-components'
  import { IconLoading } from '@gat-ship/icons'

  const { recentlyVisited } = useStakeholderRecentlyVisited()

  const props = defineProps<SelectStakeholderProps>()

  const emit = defineEmits<{
    (e: 'select', item: SelectStakeholderItem): void
  }>()

  const selectedStakeholderId = defineModel<
    SelectStakeholderItem['id'] | undefined
  >({
    required: true
  })

  const selectedStakeholder = computed(() => {
    return (
      props.items?.find((i) => i.id === selectedStakeholderId.value) ??
      undefined
    )
  })

  const {
    searchInputValue,
    searchResults,
    isSearching,
    clearAll: clearSearch
  } = globalUtils.composables.useDebouncedSearchInput<
    SearchResult<Stakeholder>
  >({
    apiCall: (inputValue, signal) =>
      stakeholdersSearchRequest(
        {
          query: `${inputValue}`,
          queryMode: 'free-text'
        },
        {
          signal
        }
      )
  })

  const isOpen = defineModel<boolean>('open', { default: false })

  const _items = computed((): ComboboxFieldItem<SelectStakeholderItem>[] => {
    return (
      props.items?.map((i) => ({
        title: i.name,
        value: i.id,
        searchValue: i.name,
        metaData: i
      })) ?? []
    )
  })

  const recentlyVisitedItems = computed(
    (): ComboboxFieldItem<SelectStakeholderItem>[] => {
      return (
        recentlyVisited.value.map((i) => ({
          title: i.text,
          value: i.id,
          searchValue: i.text,
          metaData: {
            id: i.id,
            name: i.text,
            stakeholderType: i.type
          }
        })) ?? []
      )
    }
  )

  function selectStakeholder(item: SelectStakeholderItem) {
    selectedStakeholderId.value = item.id
    isOpen.value = false
    clearSearch()
    emit('select', item)
  }

  const searchInputRef = useTemplateRef<HTMLInputElement>('searchInputRef')

  watch(isOpen, (newVal) => {
    if (newVal) {
      setTimeout(() => {
        searchInputRef.value?.focus()
      }, 300)
    }
  })

  const showRecentlyVisited = computed(() => {
    return props.enableRecentlyVisited && recentlyVisitedItems.value.length > 0
  })
</script>
<template>
  <ComboboxField
    v-bind="props"
    v-model="selectedStakeholderId"
    v-model:open="isOpen"
    :data-testid="props.dataTestid ?? 'select-stakeholder-input'"
    :items="_items"
  >
    <template v-if="selectedStakeholder" #default>
      <StakeholderSelectItem
        :stakeholder-type="selectedStakeholder.stakeholderType"
        :name="selectedStakeholder.name"
      />
    </template>
    <template #content>
      <Command class="w-full" :highlight-on-hover="true">
        <div
          data-slot="command-input-wrapper"
          class="flex h-12 items-center gap-2 border-b px-3"
        >
          <icon-search class="size-4 shrink-0 opacity-50" />
          <input
            ref="searchInputRef"
            v-model="searchInputValue"
            placeholder="Search Stakeholder"
            class="placeholder:text-muted-foreground outline-hidden flex h-12 w-full rounded-md bg-transparent py-3 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        <CommandList>
          <div
            v-if="isSearching"
            class="text-muted-foreground flex items-center gap-2 px-2 py-3 text-xs"
          >
            <IconLoading class="size-4" />
            Searching...
          </div>
          <CommandGroup
            v-else-if="searchInputValue.length > 0"
            heading="Search Results"
          >
            <div
              v-if="searchResults.length === 0"
              class="text-muted-foreground px-3 py-3 text-center text-xs"
            >
              No results found
            </div>
            <CommandItem
              v-for="item in searchResults"
              :key="item.id"
              :value="item.id"
              @select.prevent="
                selectStakeholder({
                  id: item.id,
                  name: item.metadata!.name,
                  stakeholderType: item.metadata!.stakeholderType
                })
              "
            >
              <StakeholderSelectItem
                :stakeholder-type="item.metadata!.stakeholderType"
                :name="item.title"
              />
            </CommandItem>
          </CommandGroup>
          <CommandGroup v-if="showRecentlyVisited" heading="Recently Visited">
            <CommandItem
              v-for="item in recentlyVisitedItems"
              :key="item.value"
              :value="item.value"
              @select.prevent="selectStakeholder(item.metaData)"
            >
              <StakeholderSelectItem
                :stakeholder-type="item.metaData?.stakeholderType"
                :name="item.title"
              />
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </template>
  </ComboboxField>
</template>
